/**
 * BillingPage Component
 * 
 * Usage-based billing page for YAPN dashboard.
 * Shows current usage, payment methods, and invoice history.
 * Integrated with Stripe for payment processing.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CreditCard, Calendar, Clock, DollarSign, AlertCircle, X } from 'lucide-react';
import { formatCurrency, formatNumber } from '../utils/formatters';
import PaymentMethodSetup from '../components/billing/PaymentMethodSetup';
import { useSettings } from '../contexts/SettingsContext';
import EmptyState from '../components/common/EmptyState';

const BillingPage = ({ requiresPayment = false }) => {
  const { getAccessTokenSilently } = useAuth0();
  const { selectedLocationId } = useSettings();
  const [usage, setUsage] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);

  const apiUrl = process.env.REACT_APP_API_URL;

  const fetchBillingData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      if (!selectedLocationId || selectedLocationId === 'all') {
        setError('Please select a specific location to view billing.');
        setIsLoading(false);
        return;
      }

      const token = await getAccessTokenSilently();

      // Fetch payment status BY LOCATION
      const paymentResponse = await fetch(
        `${apiUrl}/api/billing/payment-status/location/${selectedLocationId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          }
        }
      );

      if (paymentResponse.ok) {
        const paymentData = await paymentResponse.json();
        
        if (paymentData.has_payment_method && paymentData.payment_method) {
          setPaymentMethod({
            card_brand: paymentData.payment_method.card.brand,
            last4: paymentData.payment_method.card.last4,
            exp_month: paymentData.payment_method.card.exp_month,
            exp_year: paymentData.payment_method.card.exp_year
          });
        } else {
          setPaymentMethod(null);
        }
      }

      // Fetch current usage BY LOCATION
      const usageResponse = await fetch(
        `${apiUrl}/api/billing/current-usage/location/${selectedLocationId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          }
        }
      );

      if (usageResponse.ok) {
        const usageData = await usageResponse.json();
        setUsage(usageData);
      }

    } catch (err) {
      console.error('Error fetching billing data:', err);
      setError('Unable to load billing information. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  }, [selectedLocationId, getAccessTokenSilently, apiUrl]);

  // Fetch billing data when location changes
  useEffect(() => {
    if (selectedLocationId && selectedLocationId !== 'all') {
      fetchBillingData();
    }
  }, [selectedLocationId, fetchBillingData]);

  const handleUpdatePaymentMethod = async () => {
    try {
      setError(null);
      
      if (!selectedLocationId || selectedLocationId === 'all') {
        setError('Please select a specific location to update payment method.');
        return;
      }

      const token = await getAccessTokenSilently();
      
      // Create Setup Intent FOR THIS LOCATION
      const response = await fetch(
        `${apiUrl}/api/billing/create-setup-intent/location/${selectedLocationId}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.text();
        console.error('Backend error response:', errorData);
        throw new Error('PAYMENT_SETUP_FAILED');
      }

      const data = await response.json();
      setClientSecret(data.client_secret);
      setShowPaymentForm(true);
    } catch (err) {
      console.error('Error creating setup intent:', err);
      setError('Unable to set up payment method. Please try again later.');
    }
  };

  const handlePaymentSuccess = () => {
    setShowPaymentForm(false);
    setClientSecret(null);
    // Refresh payment data
    fetchBillingData();
  };

  const handlePaymentCancel = () => {
    setShowPaymentForm(false);
    setClientSecret(null);
  };

  // Handle "All Locations" selection - require specific location for billing
  if (!selectedLocationId || selectedLocationId === 'all') {
    return (
      <div className="p-4 lg:p-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl text-white mb-2">Billing & Usage</h1>
            <p className="text-gray-400">
              Manage your payment methods and view usage-based billing
            </p>
          </div>
          <EmptyState
            icon={<CreditCard className="w-8 h-8 text-gray-400" />}
            title="Select a Location"
            description="Please select a specific location from the sidebar to view billing information. Each location has separate billing."
          />
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3AD0E6]"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl text-white mb-2">Billing & Usage</h1>
          <p className="text-gray-400">
            Manage your payment methods and view usage-based billing
          </p>
        </div>

        {/* Payment Required Warning */}
        {requiresPayment && !paymentMethod && (
          <Card className="mb-6 bg-yellow-900/20 border-yellow-700 p-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-yellow-400 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-semibold text-yellow-400 mb-2">
                  Payment Method Required
                </h3>
                <p className="text-yellow-300 mb-3">
                  Please add a payment method to access your dashboard features. 
                  Your account has been restricted until payment information is provided.
                </p>
                <p className="text-sm text-yellow-400">
                  Once you add a payment method below, you'll have full access to all features.
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Error Message */}
        {error && (
          <Card className="mb-6 bg-red-900/20 border-red-700 p-6">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-400" />
              <p className="text-red-400">{error}</p>
            </div>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Current Usage Card */}
          <Card className="lg:col-span-2 bg-gray-800/50 border-gray-700 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-white">Current Usage</h2>
              <div className="flex items-center gap-2 text-gray-400 text-sm">
                <Calendar className="w-4 h-4" />
                {usage?.billing_period && (
                  <span>
                    {usage.billing_period.start} - {usage.billing_period.end}
                  </span>
                )}
              </div>
            </div>

            {/* Usage Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {/* Total Minutes */}
              <div className="bg-gray-900/50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4 text-[#3AD0E6]" />
                  <span className="text-gray-400 text-xs">Minutes Used</span>
                </div>
                <p className="text-2xl font-bold text-white">
                  {usage ? formatNumber(usage.minutes_used) : '—'}
                </p>
              </div>

              {/* Plan */}
              <div className="bg-gray-900/50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <CreditCard className="w-4 h-4 text-[#3AD0E6]" />
                  <span className="text-gray-400 text-xs">Plan</span>
                </div>
                <p className="text-2xl font-bold text-white capitalize">
                  {usage?.subscription_tier || '—'}
                </p>
              </div>

              {/* Rate */}
              <div className="bg-gray-900/50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="w-4 h-4 text-[#3AD0E6]" />
                  <span className="text-gray-400 text-xs">Rate/Minute</span>
                </div>
                <p className="text-2xl font-bold text-white">
                  {usage ? formatCurrency(usage.rate_per_minute * 100) : '—'}
                </p>
              </div>

              {/* Total Cost */}
              <div className="bg-gradient-to-r from-[#3AD0E6]/20 to-[#3630A4]/20 rounded-lg p-4 border border-[#3AD0E6]/30">
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="w-4 h-4 text-[#3AD0E6]" />
                  <span className="text-gray-400 text-xs">Current Total</span>
                </div>
                <p className="text-2xl font-bold text-white">
                  {usage ? formatCurrency(usage.current_total * 100) : '—'}
                </p>
              </div>
            </div>

            {/* Usage Bar */}
            <div className="bg-gray-900/50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-400">Billing Cycle Progress</span>
                <span className="text-sm text-gray-400">
                  {usage?.billing_period ? `${usage.billing_period.progress_percent.toFixed(1)}%` : '—'}
                </span>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-2">
                <div 
                  className="bg-gradient-to-r from-[#3AD0E6] to-[#3630A4] h-2 rounded-full transition-all"
                  style={{ 
                    width: usage?.billing_period ? `${usage.billing_period.progress_percent}%` : '0%'
                  }}
                />
              </div>
            </div>
          </Card>

          {/* Payment Method Card */}
          <Card className="bg-gray-800/50 border-gray-700 p-6">
            <h2 className="text-xl font-semibold text-white mb-6">Payment Method</h2>
            
            {paymentMethod ? (
              <div className="space-y-4">
                <div className="bg-gray-900/50 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-8 bg-gradient-to-r from-[#3AD0E6] to-[#3630A4] rounded flex items-center justify-center">
                      <CreditCard className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-white font-medium capitalize">{paymentMethod.card_brand}</p>
                      <p className="text-gray-400 text-sm">•••• {paymentMethod.last4}</p>
                    </div>
                  </div>
                  <p className="text-gray-400 text-sm">
                    Expires {paymentMethod.exp_month}/{paymentMethod.exp_year}
                  </p>
                </div>

                <Button 
                  variant="outline"
                  className="w-full bg-gray-700 border-gray-600 text-white hover:bg-gray-600"
                  onClick={handleUpdatePaymentMethod}
                >
                  Update Payment Method
                </Button>
              </div>
            ) : (
              <div className="text-center py-8">
                <CreditCard className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400 mb-4">No payment method on file</p>
                <Button 
                  className="bg-gradient-to-r from-[#3AD0E6] to-[#3630A4] text-white"
                  onClick={handleUpdatePaymentMethod}
                >
                  Add Payment Method
                </Button>
              </div>
            )}
          </Card>
        </div>

        {/* Invoice History */}
        <Card className="bg-gray-800/50 border-gray-700 p-6">
          <h2 className="text-xl font-semibold text-white mb-6">Invoice History</h2>
          <div className="text-center py-12">
            <Calendar className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">No invoices yet</p>
          </div>
        </Card>

        {/* Payment Form Modal */}
        {showPaymentForm && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <Card className="bg-gray-800 border-gray-700 p-6 max-w-md w-full">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-white">
                  {paymentMethod ? 'Update' : 'Add'} Payment Method
                </h2>
                <button
                  onClick={handlePaymentCancel}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <PaymentMethodSetup
                locationId={selectedLocationId}
                clientSecret={clientSecret}
                onSuccess={handlePaymentSuccess}
                onCancel={handlePaymentCancel}
                getAccessTokenSilently={getAccessTokenSilently}
              />
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default BillingPage;

